import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  formatMoney,
  offersByChain,
  toCzkMinor,
  type CatalogItem,
  type FxRates,
  type Offer,
  type Store,
  type TripSummary,
} from '@sp/shared'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { addOffersToTrip, offerPlace, openTrips } from '../lib/offerToTrip.ts'
import { chainLabel, offerPack, offerTitle, offerValidity } from '../lib/offers.ts'
import { capitalize, formatShortWeekday, joinNames, plural, tripName, tripSub } from '../lib/format.ts'
import { invalidateResources, errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { OfferImage } from './OfferImage.tsx'
import { inDealText } from './DealsSheet.tsx'
import { Button, Checkbox, Paragraph, SelectField, TextCol } from './ui.tsx'

const NEW_TRIP = 'novy'

/** Akce polozky po retezcich (poradi z offersByChain: obchod polozky prvni), s indexem pro vyber. */
function chainGroups(options: readonly { offer: Offer; own: boolean }[]) {
  const groups: { chain: string; own: boolean; entries: { option: { offer: Offer; own: boolean }; index: number }[] }[] = []
  options.forEach((option, index) => {
    const last = groups[groups.length - 1]
    if (last && last.chain === option.offer.chain) last.entries.push({ option, index })
    else groups.push({ chain: option.offer.chain, own: option.own, entries: [{ option, index }] })
  })
  return groups
}

export interface TemplateDealItem {
  item: CatalogItem
  offers: Offer[]
}

/**
 * Kontrola slev v sablone (navrh v3) — stejny panel jako v nakupu, jen posledni
 * krok je „Přidat na nákup". Predvybrana je jen nejlevnejsi akce v obchode polozky.
 * Rodic ho vykresli jen otevreny (vyber se tak pri kazdem otevreni zacne znovu).
 */
export function TemplateDealsSheet({
  groupId,
  entries,
  stores,
  trips,
  rates,
  onClose,
}: {
  groupId: string
  entries: readonly TemplateDealItem[]
  stores: readonly Store[]
  trips: readonly TripSummary[]
  rates: FxRates
  onClose(): void
}) {
  const navigate = useNavigate()
  const targets = openTrips(trips)
  const [target, setTarget] = useState<string>(targets[0]?.id ?? NEW_TRIP)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const groups = entries.map((entry) => {
    const store = entry.item.storeId ? (stores.find((s) => s.id === entry.item.storeId) ?? null) : null
    const byChain = offersByChain(entry.offers, store?.offerChain ?? null, rates)
    const options = byChain.flatMap((group) => group.offers.map((offer) => ({ offer, own: group.own })))
    return { ...entry, store, options, preselected: options.findIndex((o) => o.own) }
  })
  // Jen zmeny oproti predvyberu (jako v nakupu).
  const [chosen, setChosen] = useState<ReadonlyMap<string, number | null>>(new Map())
  const pickedIndex = (id: string, preselected: number) =>
    chosen.has(id) ? (chosen.get(id) ?? null) : preselected >= 0 ? preselected : null
  const picks = groups.flatMap((g) => {
    const index = pickedIndex(g.item.id, g.preselected)
    const option = index === null ? undefined : g.options[index]
    return option ? [{ offer: option.offer, catalogItem: g.item }] : []
  })

  const trip = targets.find((t) => t.id === target) ?? null
  const routeAdds = trip
    ? [...new Set(picks.flatMap((p) => {
        const place = offerPlace(p.offer, trip, stores)
        return place?.addsStore && place.store ? [place.store.name] : []
      }))]
    : []

  async function submit() {
    if (picks.length === 0) return
    setBusy(true)
    setMessage(null)
    try {
      let tripId = trip?.id
      if (!tripId) {
        // Novy nakup rovnou s obchody, kde akce plati.
        const storeIds = [
          ...new Set(picks.flatMap((p) => stores.find((s) => !s.archived && s.offerChain === p.offer.chain)?.id ?? [])),
        ]
        const created = await api.trips.create(groupId, { kind: storeIds.length > 1 ? 'big' : 'quick', storeIds })
        tripId = created.id
      }
      const result = await addOffersToTrip(tripId, picks, stores)
      invalidateResources(`sp:nakupy:${groupId}`)
      if (!trip) {
        onClose()
        navigate(`/nakup/${tripId}`)
        return
      }
      const parts = [
        result.added > 0 ? `přidáno: ${plural(result.added, 'položka', 'položky', 'položek')}` : null,
        result.updated.length > 0 ? `s cenou z akce: ${joinNames(result.updated.map((i) => i.name))}` : null,
        result.inBasket.length > 0 ? `už v košíku: ${joinNames(result.inBasket.map((i) => i.name))}` : null,
        result.routeAdded.length > 0 ? `na trasu přibyl ${joinNames(result.routeAdded.map((s) => s.name))}` : null,
      ].filter(Boolean)
      setMessage({ ok: true, text: `${capitalize(parts.join(' · '))} (${tripName(trip, stores).title})` })
      setChosen(new Map(groups.map((g) => [g.item.id, null])))
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof HttpError && err.isOffline ? 'Na nákup jde přidávat jen se signálem.' : errorMessage(err),
      })
    } finally {
      setBusy(false)
    }
  }

  const tripOptions = [
    ...targets.map((t) => ({
      value: t.id,
      label: tripName(t, stores).title,
      sub: tripSub(t, stores),
    })),
    { value: NEW_TRIP, label: 'Nový nákup', sub: 's obchody, kde akce platí' },
  ]

  const footer = (
    <div className="deals-footer">
      <SelectField ariaLabel="Na který nákup" value={target} onChange={setTarget} options={tripOptions} />
      {routeAdds.length > 0 && <Paragraph size="meta">{`Na trasu se přidá ${joinNames(routeAdds)}.`}</Paragraph>}
      {message && <Paragraph tone={message.ok ? 'fg' : 'error'}>{message.text}</Paragraph>}
      <Button kind="primary" full disabled={busy || picks.length === 0} onClick={() => void submit()}>
        {picks.length === 0
          ? 'Vyber, co přidat'
          : trip
            ? `Přidat ${picks.length} ${trip.plannedDate ? `na ${formatShortWeekday(trip.plannedDate)}` : 'na nákup'}`
            : `Založit nákup s ${plural(picks.length, 'položkou', 'položkami', 'položkami')}`}
      </Button>
    </div>
  )

  return (
    <Sheet open onClose={onClose} title={inDealText(entries.length)} subtitle="v obchodech skupiny" footer={footer} className="deals-sheet">
      <div className="deal-grid">
        {groups.map((group) => {
          const picked = pickedIndex(group.item.id, group.preselected)
          const select = (index: number | null) => setChosen((prev) => new Map(prev).set(group.item.id, index))
          return (
            <div key={group.item.id} className={`deal-card${picked !== null ? ' is-selected' : ''}`}>
              <button
                type="button"
                className="deal-card-head"
                aria-pressed={picked !== null}
                onClick={() => select(picked !== null ? null : Math.max(0, group.preselected))}
              >
                <Checkbox checked={picked !== null} />
                <TextCol
                  title={group.item.name}
                  sub={[group.store?.name ?? 'bez obchodu', plural(group.options.length, 'akce', 'akce', 'akcí')].join(' · ')}
                />
              </button>
              <div className="deal-options" role="radiogroup" aria-label={`Akce pro ${group.item.name}`}>
                {chainGroups(group.options).map((chain) => (
                  <div key={chain.chain} className="deal-store" role="group" aria-label={chainLabel(chain.chain)}>
                    <div className="deal-store-head">
                      <span className="deal-store-name">
                        {chain.own && group.store ? group.store.name : chainLabel(chain.chain)}
                      </span>
                      {chain.own && <span className="offer-tag is-best">obchod položky</span>}
                    </div>
                    {chain.entries.map(({ option, index }) => {
                      const { offer } = option
                      const czk =
                        offer.currency !== 'CZK' && offer.priceMinor !== null
                          ? toCzkMinor(offer.priceMinor, offer.currency, rates)
                          : null
                      return (
                        <button
                          key={offer.id}
                          type="button"
                          role="radio"
                          aria-checked={picked === index}
                          className={`deal-option${picked === index ? ' is-active' : ''}`}
                          onClick={() => select(index)}
                        >
                          <span className="deal-radio" aria-hidden="true" />
                          <OfferImage offer={offer} size="thumb" />
                          <TextCol
                            title={offerTitle(offer)}
                            sub={[offerPack(offer), offerValidity(offer), offer.requiresCard ? 'jen s kartou' : null]
                              .filter(Boolean)
                              .join(' · ')}
                          />
                          <span className="deal-price">
                            <strong>{offer.priceMinor === null ? 'v letáku' : formatMoney(offer.priceMinor, offer.currency)}</strong>
                            {czk !== null && <span className="muted">{`≈ ${Math.round(czk / 100)} Kč`}</span>}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </Sheet>
  )
}

